import { ValidationSchema, Tuple } from '../../types';
import { EntityDetailsModel, DeclareAndSubmitApplicationModel } from '../../api/models';
import {
    textMandatory,
    fieldRequiredValidatorConditional,
    minLengthWithTrim,
    maxLengthWithTrim,
    nameBasicFormat,
    nameNo3ConsecutiveChars,
    nameAtLeastOneChar,
    nameNoConsecutivePunctuationSpace,
    phoneNumberFormat,
    emailValidation,
    alphanumericBasic,
    validateEntityModel,
    maxLengthIgnoreBracketsSpaceAndPlus,
    minLengthIgnoreBracketsSpaceAndPlus,
    mustBeTrueValidator,
} from '../validationFunctions';
import { interpolateValidationRule } from '../validatorMessages';
import * as validatorRules from '../validatorRules.json';
import { interpolateValidationString } from '../util';

const mandatoryValidator: Tuple<(_value: any) => boolean, string>[] = [
    [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
];

const titleOtherValidator = (min: number, max: number, condition?: () => boolean): Tuple<(value: any) => boolean, string>[] => {
    if (!condition || condition()) {
        return [
            [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
            [minLengthWithTrim(min), interpolateValidationRule(validatorRules.BR2_Minimum_Field_Length, [], [min.toString()])],
            [maxLengthWithTrim(max), interpolateValidationRule(validatorRules.BR3_Maximum_Field_Length, [], [max.toString()])],
            [alphanumericBasic, validatorRules.BR11_Alphanumeric_Basic_Format.FieldMessage],
        ];
    }
    return [];
};

const namePersonValidator = (min: number, max: number, condition?: () => boolean): Tuple<(value: any) => boolean, string>[] => {
    if (!condition || condition()) {
        const bR14NamePersonNo3ConsecutiveMsg = validatorRules.BR14_Name_Person_No_3_Consecutive_Characters.FieldMessage;
        const bR14NamePersonAtLeastOneCharMsg = validatorRules.BR14_Name_Person_At_Least_One_Character.FieldMessage;
        const bR14NamePersonNoConsecutiveMsg = validatorRules.BR14_Name_Person_No_Consecutive_Punctuation_Or_Spaces.FieldMessage;
        return [
            [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
            [minLengthWithTrim(min), interpolateValidationRule(validatorRules.BR2_Minimum_Field_Length, [], [min.toString()])],
            [maxLengthWithTrim(max), interpolateValidationRule(validatorRules.BR3_Maximum_Field_Length, [], [max.toString()])],
            [nameBasicFormat, interpolateValidationString(validatorRules.BR14_Name_Person_Format.FieldMessage, [['PropertyName', '{{label}}']], [])],
            [nameNo3ConsecutiveChars, interpolateValidationString(bR14NamePersonNo3ConsecutiveMsg, [['PropertyName', '{{label}}']], [])],
            [nameAtLeastOneChar, interpolateValidationString(bR14NamePersonAtLeastOneCharMsg, [['PropertyName', '{{label}}']], [])],
            [nameNoConsecutivePunctuationSpace, interpolateValidationString(bR14NamePersonNoConsecutiveMsg, [['PropertyName', '{{label}}']], [])],
        ];
    }
    return [];
};

const positionValidator = (min: number, max: number, condition?: () => boolean): Tuple<(value: any) => boolean, string>[] => {
    if (!condition || condition()) {
        return [
            [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
            [minLengthWithTrim(min), interpolateValidationRule(validatorRules.BR2_Minimum_Field_Length, [], [min.toString()])],
            [maxLengthWithTrim(max), interpolateValidationRule(validatorRules.BR3_Maximum_Field_Length, [], [max.toString()])],
            [alphanumericBasic, validatorRules.BR11_Alphanumeric_Basic_Format.FieldMessage],
        ];
    }
    return [];
};

const phoneValidator = (condition?: () => boolean): Tuple<(value: any) => boolean, string>[] => {
    if (!condition || condition()) {
        return [
            [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
            [minLengthIgnoreBracketsSpaceAndPlus(10), interpolateValidationRule(validatorRules.BR2_Minimum_Field_Length, [], ['10'])],
            [maxLengthIgnoreBracketsSpaceAndPlus(18), interpolateValidationRule(validatorRules.BR3_Maximum_Field_Length, [], ['18'])],
            [phoneNumberFormat, interpolateValidationString(validatorRules.BR13_Phone_Number_Format.FieldMessage, [['PropertyName', '{{label}}']], [])],
        ];
    }
    return [];
};

const emailValidator = (condition?: () => boolean): Tuple<(value: any | undefined) => boolean, string>[] => {
    if (!condition || condition()) {
        return [
            [textMandatory, validatorRules.BR1_Mandatory_Field.FieldMessage],
            [emailValidation, interpolateValidationString(validatorRules.BR10_Email_Format.FieldMessage, [['PropertyName', '{{label}}']], [])],
        ];
    }
    return [];
};

const entityValidator = (condition?: () => boolean): Tuple<(value: Partial<EntityDetailsModel> | undefined) => boolean, string>[] => {
    if (!condition || condition()) {
        return [
            [validateEntityModel, validatorRules.BR1_Mandatory_Field.FieldMessage],
        ];
    }
    return [];
};

export const rndDeclaration: ValidationSchema<Partial<DeclareAndSubmitApplicationModel>> = (_values: Partial<DeclareAndSubmitApplicationModel>) => {
    const isDeclarantNotTheSameAsPrimaryCompanyContact = _values.isDeclarantSameAsPrimaryCompanyContact === 'No';
    return {
        isDeclarantSameAsPrimaryCompanyContact: mandatoryValidator,
        titleOther: titleOtherValidator(2, 30, () => isDeclarantNotTheSameAsPrimaryCompanyContact),
        firstName: namePersonValidator(2, 50, () => isDeclarantNotTheSameAsPrimaryCompanyContact),
        lastName: namePersonValidator(2, 50, () => isDeclarantNotTheSameAsPrimaryCompanyContact),
        phone: phoneValidator(() => isDeclarantNotTheSameAsPrimaryCompanyContact),
        position: positionValidator(2, 50, () => isDeclarantNotTheSameAsPrimaryCompanyContact),
        entityDetails: entityValidator(() => isDeclarantNotTheSameAsPrimaryCompanyContact),
        email: emailValidator(() => isDeclarantNotTheSameAsPrimaryCompanyContact),
        declarantRelationshipWithCompany: fieldRequiredValidatorConditional(() => isDeclarantNotTheSameAsPrimaryCompanyContact),
        hasAcceptedDeclaration: mustBeTrueValidator,
    };
};

export const rndDeclarationSoft: ValidationSchema<Partial<DeclareAndSubmitApplicationModel>> = (_values: Partial<DeclareAndSubmitApplicationModel>) => {
    return {
        isDeclarantSameAsPrimaryCompanyContact: mandatoryValidator,
        firstName: mandatoryValidator,
        lastName: mandatoryValidator,
        position: mandatoryValidator,
        phone: mandatoryValidator,
        email: mandatoryValidator,
        entityDetails: mandatoryValidator,
        titleOther: mandatoryValidator,
        declarantRelationshipWithCompany: mandatoryValidator,
        hasAcceptedDeclaration: mustBeTrueValidator,
    };
};
